import React from "react";
import { I18n } from "react-redux-i18n";

import { Flex } from "@common/components/Flex";
import { Button } from "@common/components/Button";
import {
  QueryManagement,
  QueryManagementFormat,
  QueryManagementProps,
} from "@common/components/query-management";
import { getTestAttributes } from "@common/utils/tests";
import { MODULE_NAME } from "@modules/invite-status/constants/shared";
import { SkipLogic } from "@common/components/skip-logic";
import {
  SkipLogicCondition,
  SkipLogicConditionCreateDto,
  SkipLogicField,
  SkipLogicPredefinedControlType,
} from "@common/components/skip-logic/skip-logic.types";
import { generateId } from "@common/utils/common";
import { HttpErrorDto } from "@common/dto/http";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { QueryManagementQueryFormatGetDto } from "@modules/invite-status/services/formats";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { FieldType } from "@common/dto/field/type";

import "./query-control.styles.scss";

export type QueryControlProps = {
  createFormatFolder: QueryManagementProps["createFormatFolder"];
  deleteFormat: QueryManagementProps["deleteFormat"];
  deleteFormatFolder: QueryManagementProps["deleteFormatFolder"];
  getFolders: QueryManagementProps["getFolders"];
  getFormatFolders: () => Promise<FolderPrimaryListDto[]>;
  getFormats: QueryManagementProps["getFormats"];
  getFields: (params: {
    folderId?: FolderPrimaryListDto["id"];
    searchQuery?: string;
  }) => Promise<Array<FieldPrimaryListDto>>;
  showInfoMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
  queries: Array<SkipLogicCondition>;
  onQueriesChange: (queries: Array<SkipLogicCondition>) => void;
  getQueryFormat?: () => Promise<QueryManagementQueryFormatGetDto>;
  saveQueryFormats: (params: {
    format: Partial<QueryManagementFormat>;
    queries: Array<SkipLogicCondition>;
  }) => Promise<void>;
  submitText?: string;
  onSelectedFolderChange?: (params: {
    searchKeyword: string;
    folder: FolderPrimaryListDto;
  }) => void;
  onSelectedFormatChange?: (params: {
    format: Partial<QueryManagementFormat>;
  }) => void;
  submitBtnDisabled?: boolean;
  formatNameSearchPlaceholder?: string;
  formatDescriptionSearchPlaceholder?: string;
  onQueryManagementClose?: () => void;
};

export const QueryControl: React.FC<QueryControlProps> = (props) => {
  const {
    onSelectedFolderChange,
    onSelectedFormatChange,
    submitBtnDisabled,
    showErrorMessage,
    showInfoMessage,
    getFolders,
    getFormats,
    deleteFormatFolder,
    deleteFormat,
    createFormatFolder,
    queries,
    onQueriesChange,
    getFields,
    getQueryFormat,
    submitText,
    saveQueryFormats,
    getFormatFolders,
    formatNameSearchPlaceholder,
    formatDescriptionSearchPlaceholder,
    onQueryManagementClose,
  } = props;
  const [managementOpened, setManagementOpened] = React.useState(false);

  const openManagement = React.useCallback(() => {
    setManagementOpened(true);
  }, []);

  const closeManagement = React.useCallback(() => {
    setManagementOpened(false);
    if (onQueryManagementClose) {
      onQueryManagementClose();
    }
  }, [onQueryManagementClose]);

  const handleQueryManagementSubmit = React.useCallback(
    async (params: {
      searchKeyword?: string;
      folder?: FolderPrimaryListDto;
      format: QueryManagementFormat;
    }) => {
      try {
        await saveQueryFormats({
          format: params.format,
          queries: queries,
        });
        closeManagement();
      } catch (err) {
        console.error(err);
        const errorMessage =
          (err as HttpErrorDto | undefined)?.entity ||
          I18n.t("Failed to save query format");
        showErrorMessage(errorMessage);
      }
    },
    [closeManagement, saveQueryFormats, queries]
  );

  const handleSkipLogicConditionAdd = React.useCallback(
    ({ conditions }: { conditions: SkipLogicConditionCreateDto[] }) => {
      onQueriesChange([
        ...queries,
        ...conditions.map((condition) => ({ ...condition, id: generateId() })),
      ]);
    },
    [onQueriesChange, queries]
  );

  const handleGetQueryFormat = React.useCallback(() => {
    if (!getQueryFormat) {
      return;
    }

    getQueryFormat()
      .then((res) => {
        onQueriesChange(
          res.clauses.map((condition) => ({
            ...condition,
            id: generateId(),
          })) as SkipLogicCondition[]
        );
      })
      .catch((err: unknown) => {
        console.error(err);
        const errorMessage =
          (err as HttpErrorDto | undefined)?.entity ||
          I18n.t("Failed to get query format");
        showErrorMessage(errorMessage);
      });
  }, [showErrorMessage, getQueryFormat]);

  React.useEffect(() => {
    handleGetQueryFormat();
  }, []);

  const getFolderFields = React.useCallback(
    (params: { folderId?: number; system?: boolean; searchQuery?: string }) => {
      return getFields(params).then((fields) => {
        return fields.map((field): SkipLogicField => {
          return {
            fieldId: field.id,
            fieldName: field.name,
            fieldContext: field.context,
            fieldTypeId: field.type,
            fieldTypeName: FieldType[field.type] as keyof typeof FieldType,
            systemField: field.system,
            columnWidth: 0,
            imageURL: field.imageURL,
            options: field.options,
            parentField: field.parentField,
          };
        });
      });
    },
    []
  );

  return (
    <>
      {managementOpened && (
        <QueryManagement
          submitText={submitText}
          withSearch
          onClose={closeManagement}
          showErrorMessage={showErrorMessage}
          showInfoMessage={showInfoMessage}
          getFolders={getFormatFolders}
          deleteFormatFolder={deleteFormatFolder}
          deleteFormat={deleteFormat}
          createFormatFolder={createFormatFolder}
          getFormats={getFormats}
          getId={{
            creatingFolderName: getTestAttributes(
              MODULE_NAME,
              "query-management-creating-folder-name"
            ).id,
            creatingFolderItem: getTestAttributes(
              MODULE_NAME,
              "query-management-creating-folder-item"
            ).id,
            creatingFolderCollapseItem: getTestAttributes(
              MODULE_NAME,
              "query-management-creating-collapse-item"
            ).id,
            creatingFolderCancelBtn: getTestAttributes(
              MODULE_NAME,
              "query-management-creating-folder-cancel-btn"
            ).id,
            creatingFolderCreateBtn: getTestAttributes(
              MODULE_NAME,
              "query-management-creating-folder-create-btn"
            ).id,
            modalTitle: getTestAttributes(
              MODULE_NAME,
              "query-management",
              "title"
            ).id,
            folder: getTestAttributes(MODULE_NAME, "query-management", "folder")
              .id,
            search: getTestAttributes(MODULE_NAME, "query-management", "search")
              .id,
            searchIcon: getTestAttributes(
              MODULE_NAME,
              "query-management",
              "search-icon"
            ).id,
          }}
          onSubmit={handleQueryManagementSubmit}
          moduleName={MODULE_NAME}
          onSelectedFolderChange={onSelectedFolderChange}
          onSelectedFormatChange={onSelectedFormatChange}
          submitBtnDisabled={submitBtnDisabled}
          formatNameSearchPlaceholder={formatNameSearchPlaceholder}
          formatDescriptionSearchPlaceholder={
            formatDescriptionSearchPlaceholder
          }
        />
      )}
      <div className="InviteStatusQueryControl">
        <Flex justify="space-between">
          <h6 className="InviteStatusQueryControl__title">
            {I18n.t("reviewEditQueryIndividuals")}
          </h6>
          <Button onClick={openManagement}>{I18n.t("Save Format")}</Button>
        </Flex>
        <SkipLogic
          moduleName={MODULE_NAME}
          className="InviteStatusQueryControl__skip-logic"
          conditions={queries as SkipLogicCondition[]}
          onRemove={
            onQueriesChange as (conditions: SkipLogicCondition[]) => void
          }
          onChange={
            onQueriesChange as (conditions: SkipLogicCondition[]) => void
          }
          onAdd={handleSkipLogicConditionAdd}
          getFolders={getFolders as () => Promise<FolderPrimaryListDto[]>}
          getFields={getFolderFields}
          controls={[
            {
              type: SkipLogicPredefinedControlType.moveUp,
              handler: onQueriesChange,
            },
            {
              type: SkipLogicPredefinedControlType.moveDown,
              handler: onQueriesChange,
            },
            { type: SkipLogicPredefinedControlType.add },
          ]}
        />
      </div>
    </>
  );
};
